import PlainModal from 'components/common/modal/PlainModal';
import ProjectIndication from 'components/project/ProjectIndication';
import useCheckOneInput from 'lib/hooks/useCheckOneInput';
import useInput from 'lib/hooks/useInput';
import useObjectInput from 'lib/hooks/useObjectInput';
import { makeOptionInitialValue } from 'lib/library';
import {
  IToothShade,
  Implant,
  IndicationGroup,
  IndicationPart,
  IndicationSeq,
  Tooth,
} from 'lib/types/projectTypes';
import { ICreateProjectProp } from 'lib/types/props/projectProps';
import { useDidUpdateEffect } from 'lib/utils';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { UtilActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { isClinicUserSelector } from 'store/modules/user';
import { color } from 'styles/utils';
import { Material } from 'three';
import IndicationModalContainer from './IndicationModalContainer';

interface ProjectIndicationContainerProps extends ICreateProjectProp {
  indicationInfo: object | any;
  indicationFormat: object | any;
  indicationOption: object | any;
  restorationPontic: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  occlusalSurface: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  ceramicMetal: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  clientGroupCode: {
    value: string;
    onChange: (e: any) => void;
    setValue: (value: string) => void;
  };
  isMultidie: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  isVaildTeethViewer: boolean;
  onClickIndication: (alias: string) => void;
  isProjectCreator: boolean;
}
interface IndicationInfoToothShade {
  id: string;
  list: {
    seq: number;
    name: string;
  }[];
}

function CreateProjectIndicationContainer({
  numbering,
  teeth,
  tooth,
  copyData,
  bridge,
  toothSvgRef,
  teethContextActions,
  toothShade,
  restorationPontic,
  occlusalSurface,
  ceramicMetal,
  indicationInfo,
  indicationFormat,
  indicationOption,
  isVaildTeethViewer,
  isMultidie,
  clientGroupCode,
  onClickIndication,
  scanMode,
  scanModeListCt,
  isProjectCreator,
}: ProjectIndicationContainerProps) {
  const {
    isClinicUser,
    partnerIndication,
    partnerIndicationSuccess,
    indicationOptionSuccess,
    indicationOptionOffline,
    indicationOptionOfflineSuccess,
    user: { userGroupIdx: userId, groupCode, company },
  } = useShallowAppSelector(state => ({
    isClinicUser: isClinicUserSelector(state),
    partnerIndication: state.project.partnerIndication.data || {},
    partnerIndicationSuccess: state.project.partnerIndication.success,
    user: state.user.user || {},
    indicationOptionSuccess: state.util.indicationOption.success,
    indicationOptionOffline: state.util.indicationOptionOffline.data,
    indicationOptionOfflineSuccess: state.util.indicationOptionOffline.success,
  }));

  const toothShadeList = useMemo(
    () =>
      indicationInfo?.toothShadeList.reduce(
        (acc: IToothShade[], curr: IndicationInfoToothShade) => {
          let currArr = [{ group: curr.id }, ...curr.list];
          return acc.concat(currArr);
        },
        [],
      ),
    [indicationInfo],
  );

  const materialShadeList = useMemo(
    () =>
      [...indicationInfo?.materialShadeList].reduce(
        (acc: IToothShade[], curr: IndicationInfoToothShade) => {
          let currArr = [{ group: curr.id }, ...curr.list];
          return acc.concat(currArr);
        },
        [],
      ),
    [indicationInfo?.materialShadeList],
  );

  const indicationSeq = useInput(0);
  // {id, seq, color}

  // indication
  const indication = useInput({
    id: '0',
    seq: '0',
    // color: '',
    color: color.blue,
  });
  // material
  const material = useInput(0);
  // scan abutment
  const implant = useInput(0);
  // abutment material
  const abutment = useInput(null);
  // scan scanbody
  const scanbody = useInput(null);
  // waxup scan
  const waxupScan = useInput(1); // 0: no, 1: yes,
  // scan option
  const checkSituScan = useCheckOneInput(false);
  const checkGingivaScan = useCheckOneInput(false);
  // default option
  const gapWidthCement = useInput(0.08); // 0.00 ~ 0.20 mm
  const minimalTickness = useInput(0.6); // 0.40 ~ 1.00 mm
  const millingToolDiameter = useInput(1.2); // 0.0 ~ 1.50 mm
  const virtualGingiva = useInput(0); // 0: no, 1: yes, 2: optional
  const thimbleCrown = useInput(0); // 0: no, 1: yes,
  const cervicalAdaption = useInput(0); // 1: pull to margin, 2: preserve tooth library shape
  const materialShade = useInput(0);
  const teethIsExpanded = useMemo(() => {
    return teeth.value?.map((item: Tooth) => {
      return { number: item?.number, isExpanded: false };
    });
  }, [teeth]);
  const selectedTeethIsExpanded = useInput(teethIsExpanded);
  const isOpenOption = useInput(false);
  const [winWidth, setWinWidth] = useState<number>(window.innerWidth);
  const [winHeight, setWinHeight] = useState<number>(window.innerHeight);

  // indicationSeq(group단위), indicationFormat
  const indicationSeqList: IndicationSeq[] = indicationFormat?.indication;
  // indication(part단위)
  const matchingIndicationPartList =
    indicationSeqList?.find((item: IndicationSeq) => item.seq === indicationSeq.value)?.list || [];
  const matchingMaterialList =
    matchingIndicationPartList?.find(item => item.seq === indication.value.seq)?.materialList || [];

  const matchingImplantList =
    matchingMaterialList?.find(item => item.seq === material.value)?.implantList || [];

  const matchingAbutmentList =
    matchingImplantList?.find(item => item.seq === implant.value)?.abutmentMaterialList || [];

  const matchingScanbodyList =
    matchingImplantList?.find(item => item.seq === implant.value)?.scanbodyScanList || [];
  const {
    groupList: indicationGroupList = [],
    partList: indicationPartList = [],
    materialList = [],
    implantList = [],
    implantAbutmentMaterialList = [],
    implantScanbodyScanList = [],
  } = indicationInfo as {
    groupList: IndicationGroup[];
    partList: IndicationPart[];
    materialList: Material[];
    implantList: Implant[];
    implantAbutmentMaterialList: any[];
    implantScanbodyScanList: any[];
  };

  const defaultOption = useObjectInput({});
  const advancedOption = useObjectInput({});
  const defaultValue = useObjectInput({});
  const advancedValue = useObjectInput({});

  const [indicationAlias, setIndicationAlias] = useState('ATCR');
  const [materialAlias, setMaterialAlias] = useState('PKAD');
  const [allIndicationOption, setAllIndicationOption] = useState(null);
  const [modalStyle, setModalStyle] = useState({});

  useDidUpdateEffect(() => {
    if (partnerIndicationSuccess) {
      if (!partnerIndication) return;
      gapWidthCement.setValue(partnerIndication?.gapWidthCement);
      minimalTickness.setValue(partnerIndication?.minimalTickness);
      millingToolDiameter.setValue(partnerIndication?.millingToolDiameter);
    }
  }, [partnerIndicationSuccess]);

  useDidUpdateEffect(() => {
    if (indicationOptionSuccess) {
      defaultValue.setInputs(makeOptionInitialValue(indicationOption?.default));
      advancedValue.setInputs(makeOptionInitialValue(indicationOption?.advanced));
      defaultOption.setInputs(indicationOption?.default);
      advancedOption.setInputs(indicationOption?.advanced);
    }
  }, [indicationOptionSuccess]);

  useEffect(() => {
    if (indicationOptionOfflineSuccess) {
      // const options = indicationOptionOffline?.list?.reduce((acc: any[], curr: any) => {
      //   const defaultOptions = makeAllOptionType(curr.default);
      //   const advancedOptions = makeAllOptionType(curr.advanced);

      //   defaultOptions.forEach(option => {
      //     if (!acc.some(accItem => accItem.key === option.key)) {
      //       acc.push(option);
      //     }
      //   });
      //   advancedOptions.forEach(option => {
      //     if (!acc.some(accItem => accItem.key === option.key)) {
      //       acc.push(option);
      //     }
      //   });

      //   return acc;
      // }, []);

      setAllIndicationOption(indicationOptionOffline);
    }
  }, [indicationOptionOfflineSuccess]);

  useEffect(() => {
    UtilActions.fetch_teeth_indication_option_offline_request();
  }, []);

  useEffect(() => {
    if (!indicationAlias || !materialAlias) return;

    if (indicationAlias === 'FDT') {
      if (!!materialAlias) {
        UtilActions.fetch_teeth_indication_option_request({
          indicationAlias: indicationAlias,
          materialAlias: materialAlias,
          clientGroupCode: !!clientGroupCode.value ? clientGroupCode.value : '',
        });
      }
    }
  }, [indicationAlias, materialAlias]);

  useEffect(() => {
    if (!indicationAlias) return;

    if (indicationAlias !== 'FDT') {
      UtilActions.fetch_teeth_indication_option_request({
        indicationAlias: indicationAlias,
        clientGroupCode: !!clientGroupCode.value ? clientGroupCode.value : '',
      });
    }
  }, [indicationAlias]);

  useEffect(() => {
    selectedTeethIsExpanded.setValue(teethIsExpanded);
  }, [teethIsExpanded]);

  useEffect(() => {
    function updateWidth() {
      const width = window.innerWidth > 1920 ? '100%' : 'none';
      const justifyContent = window.innerWidth > 1920 ? 'center' : 'flex-start';

      setModalStyle({ width: width, justifyContent: justifyContent });
    }

    window.addEventListener('resize', updateWidth);
    updateWidth();

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const handleToggleTooth = useCallback(
    (toothNumber: number) => {
      isOpenOption.setValue(true);

      let currentTooth = teeth.value.find((item: Tooth) => item.number === toothNumber);
      if (!!currentTooth) {
        indicationSeq.setValue(currentTooth.preparationType);
        indication.setValue({
          seq: currentTooth.reconstructionType,
          color: currentTooth.color || color.blue,
        });
        material.setValue(currentTooth.material);
        abutment.setValue(currentTooth.implantAbutmentMaterialIdx);
        scanbody.setValue(currentTooth.implantScanbodyScanIdx);
        implant.setValue(currentTooth.implantType);
        checkSituScan.setValue(!!currentTooth.situScan);
        checkGingivaScan.setValue(!!currentTooth.separateGingivaScan);
        gapWidthCement.setValue(currentTooth.gapWidthCement);
        minimalTickness.setValue(currentTooth.minimalTickness);
        millingToolDiameter.setValue(currentTooth.millingToolDiameter);
        virtualGingiva.setValue(currentTooth.virtualGingiva);
        thimbleCrown.setValue(currentTooth.thimbleCrown);
        waxupScan.setValue(currentTooth.waxupScan);
        cervicalAdaption.setValue(currentTooth.cervicalAdaption);
        materialShade.setValue(currentTooth.materialShade);
        setIndicationAlias(currentTooth.indicationAlias);
        setMaterialAlias(currentTooth.materialAlias);
      } else {
        indicationSeq.setValue(0);
        indication.setValue({});
        material.setValue(0);
        implant.setValue(0);
        abutment.setValue(null);
        scanbody.setValue(null);
        checkSituScan.setValue(false);
        checkGingivaScan.setValue(false);
        virtualGingiva.setValue(0);
        thimbleCrown.setValue(0);
        waxupScan.setValue(0);
        cervicalAdaption.setValue(0);
        materialShade.setValue(0);
        setIndicationAlias('ATCR');
        setMaterialAlias('PKAD');
      }
    },
    [teeth.value, tooth.value],
  );

  const handleChangeToothValue = useCallback((data: object | any) => {
    indicationSeq.setValue(data.preparationType);
    indication.setValue({
      seq: data.indicationIdx,
      color: data.color,
    });
    material.setValue(data.material);
    abutment.setValue(data.implantAbutmentMaterialIdx);
    scanbody.setValue(data.implantScanbodyScanIdx);
    implant.setValue(data.implantType);
    checkSituScan.setValue(!!data.situScan);
    checkGingivaScan.setValue(!!data.separateGingivaScan);
    gapWidthCement.setValue(data.gapWidthCement);
    minimalTickness.setValue(data.minimalTickness);
    millingToolDiameter.setValue(data.millingToolDiameter);
    virtualGingiva.setValue(data.virtualGingiva);
    thimbleCrown.setValue(data.thimbleCrown);
    waxupScan.setValue(data.waxupScan);
    cervicalAdaption.setValue(data.cervicalAdaption);
    materialShade.setValue(data.materialShade);
  }, []);

  const handleSelectedIndicationChange =
    (number: number) => (event: ChangeEvent<{}>, expanded: boolean) => {
      selectedTeethIsExpanded.setValue(
        selectedTeethIsExpanded.value.map((item: any) => {
          if (item.number === number) {
            return { number: number, isExpanded: expanded };
          } else {
            return item;
          }
        }),
      );
    };

  const handleChooseIndicationPart = (data = {}) => {
    const { groupSeq, partSeq, partAlias } = data as {
      groupSeq: number;
      partSeq: string;
      partAlias: string;
    };

    setIndicationAlias(partAlias);

    // UtilActions.fetch_teeth_indication_option_request({
    //   indicationAlias: partAlias,
    //   clientGroupCode: !!clientGroupCode.value ? clientGroupCode.value : '',
    // });

    indicationSeq.setValue(groupSeq);

    const currentPartList = indicationSeqList.find(item => item.seq === groupSeq)?.list || [];
    const currentPart = currentPartList.find(item => item.seq === partSeq);
    const currentPartColor = indicationPartList.find(
      (item: IndicationPart) => item.idx === currentPart?.seq,
    )?.color;
    indication.setValue({
      seq: partSeq,
      color: currentPartColor,
    });

    if (indication.value.seq !== partSeq && currentPart) {
      material.setValue(currentPart.materialList[0].seq);
      implant.setValue(currentPart.materialList[0].implantList[0].seq);
      abutment.setValue(null);
      scanbody.setValue(null);
      // abutment.setValue('');
      // scanbody.setValue('');
    }

    // close modal and open materialModal
    // if (!isClinicUser) {
    //   onClickIndication(partAlias);
    // }
    // setIndicationModalOpenType(MATERIAL);
  };

  const handleChooseMaterial = (seq: string, alias: string) => {
    if (material.value !== seq) {
      const currentPartList = matchingIndicationPartList.find(i => i.seq === indication.value.seq);
      const initialImplant = currentPartList?.materialList?.find(i => i.seq === seq)
        ?.implantList[0];
      implant.setValue(initialImplant?.seq || 0);
      abutment.setValue(null);
      scanbody.setValue(null);

      setMaterialAlias(alias);

      // setIndicationAlias(material)
      // abutment.setValue('');
      // scanbody.setValue('');
    }
    material.setValue(seq);
    // setIndicationModalOpenType('');
  };

  const handleChooseImplant = (seq: number) => {
    implant.setValue(seq);
    if (seq === 8) {
      abutment.setValue(1);
      scanbody.setValue(1);
    } else if (seq === 9) {
      abutment.setValue(null);
      scanbody.setValue(1);
    } else {
      abutment.setValue(null);
      scanbody.setValue(null);
    }
    // setIndicationModalOpenType('');
  };

  const handleChooseAbutment = (seq: number) => {
    abutment.setValue(seq);
    // setIndicationModalOpenType('');
  };

  const handleChooseScanbody = (seq: number) => {
    scanbody.setValue(seq);
    // setIndicationModalOpenType('');
  };

  const handleDeleteTooth = (e: any, number: number) => {
    e.stopPropagation();
    tooth.setValue({});
    // tooth.setValue({ number: null, hasData: false });
    if (copyData?.value?.number === number) copyData.setValue({});
    teeth.setValue((draft: Tooth[]) => draft.filter(i => i.number !== number));
    // teethContextActions.setValue(draft => {
    //   draft.copy.active = false;
    //   draft.delete.active = false;
    // });

    // 치아 삭제시 bridge도 삭제
    const exceptedBridgeDeletedTeeth = bridge.value.filter(bridgeItem => {
      const frontNumber = String(bridgeItem).slice(0, 2);
      const backNumber = String(bridgeItem).slice(2);

      return String(number) !== frontNumber && String(number) !== backNumber;
    });

    bridge.setValue(exceptedBridgeDeletedTeeth);
  };

  return (
    <>
      <PlainModal
        isOpen={isOpenOption.value}
        // width="1920"
        hideBackdrop={true}
        disableBackdropClick={false}
        customStyle={{
          display: 'flex',
          alignItems: 'center',
          overflow: 'auto',
          height: '100vh',
          backgroundColor: 'white',
          ...modalStyle,
        }}
        customPaperStyle={{
          height: '100%',
        }}
      >
        <IndicationModalContainer
          isOpenOption={isOpenOption}
          defaultOption={defaultOption}
          advancedOption={advancedOption}
          defaultValue={defaultValue}
          advancedValue={advancedValue}
          indicationAlias={indicationAlias}
          materialAlias={materialAlias}
          teeth={teeth}
          tooth={tooth}
          numbering={numbering}
          materialShadeList={materialShadeList}
          indicationFormat={indicationFormat}
          indicationInfo={indicationInfo}
          indicationOption={indicationOption}
          indicationSeqList={indicationSeqList}
          indicationSeq={indicationSeq}
          indication={indication}
          matchingIndicationPartList={matchingIndicationPartList}
          matchingMaterialList={matchingMaterialList}
          matchingImplantList={matchingImplantList}
          matchingAbutmentList={matchingAbutmentList}
          matchingScanbodyList={matchingScanbodyList}
          material={material}
          implant={implant}
          abutment={abutment}
          scanbody={scanbody}
          checkSituScan={checkSituScan}
          checkGingivaScan={checkGingivaScan}
          gapWidthCement={gapWidthCement}
          minimalTickness={minimalTickness}
          millingToolDiameter={millingToolDiameter}
          virtualGingiva={virtualGingiva}
          thimbleCrown={thimbleCrown}
          waxupScan={waxupScan}
          cervicalAdaption={cervicalAdaption}
          materialShade={materialShade}
          restorationPontic={restorationPontic}
          occlusalSurface={occlusalSurface}
          ceramicMetal={ceramicMetal}
          isMultidie={isMultidie}
          onChooseIndicationPart={handleChooseIndicationPart}
          onChooseMaterial={handleChooseMaterial}
          onChooseImplant={handleChooseImplant}
          onChooseAbutment={handleChooseAbutment}
          onChooseScanbody={handleChooseScanbody}
        />
      </PlainModal>
      <ProjectIndication
        isViewer={false}
        numbering={numbering}
        teeth={teeth}
        tooth={tooth}
        copyData={copyData}
        bridge={bridge}
        toothSvgRef={toothSvgRef}
        teethContextActions={teethContextActions}
        toothShade={toothShade}
        toothShadeList={toothShadeList}
        materialShadeList={materialShadeList}
        scanMode={scanMode}
        scanModeListCt={scanModeListCt}
        selectedTeethIsExpanded={selectedTeethIsExpanded}
        indicationInfo={indicationInfo}
        allIndicationOption={allIndicationOption}
        isVaildTeethViewer={isVaildTeethViewer}
        isMultidie={isMultidie.value}
        onToggleTooth={handleToggleTooth}
        onChangeToothValue={handleChangeToothValue}
        onSelectedIndicationChange={handleSelectedIndicationChange}
        onDeleteTooth={handleDeleteTooth}
        isProjectCreator={isProjectCreator}
      />
    </>
  );
}

export default CreateProjectIndicationContainer;
